import { useCallback, useEffect, useState } from 'react';
import { batchesAPI, moduleProgressAPI } from '../services/api';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import { MODULE_STATUS } from '../utils/moduleProgressDisplay';

/**
 * Loads linked batches + per-batch module completion matrix; updates status.
 */
export default function useModuleCompletionMatrix(initialBatchId = '') {
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState(
    initialBatchId ? String(initialBatchId) : ''
  );
  const [matrix, setMatrix] = useState(null);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingBatches(true);
      setError('');
      try {
        const res = await batchesAPI.getAll();
        if (cancelled) return;
        const list = normalizeApiListResponse(res?.data);
        setBatches(list);
        if (!selectedBatchId && list.length === 1) {
          setSelectedBatchId(String(list[0].id));
        } else if (
          selectedBatchId &&
          !list.some((b) => String(b.id) === String(selectedBatchId))
        ) {
          setSelectedBatchId(list[0] ? String(list[0].id) : '');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Could not load batches');
          setBatches([]);
        }
      } finally {
        if (!cancelled) setLoadingBatches(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only on mount / when initial batch hint changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBatchId]);

  const loadMatrix = useCallback(async (batchId) => {
    if (!batchId) {
      setMatrix(null);
      return;
    }
    setLoadingMatrix(true);
    setError('');
    try {
      const res = await moduleProgressAPI.getBatchMatrix(batchId);
      const data = res?.data?.data ?? res?.data;
      setMatrix(data || null);
    } catch (err) {
      setMatrix(null);
      setError(err?.response?.data?.message || 'Could not load module completion');
    } finally {
      setLoadingMatrix(false);
    }
  }, []);

  useEffect(() => {
    if (selectedBatchId) {
      loadMatrix(selectedBatchId);
    } else {
      setMatrix(null);
    }
  }, [selectedBatchId, loadMatrix]);

  const updateStatus = useCallback(
    async (enrollmentId, moduleId, status) => {
      const key = `${enrollmentId}:${moduleId}`;
      setSavingKey(key);
      setError('');
      setSuccess('');
      try {
        const res = await moduleProgressAPI.upsertEnrollmentProgress(enrollmentId, {
          moduleId,
          status,
        });
        const payload = res?.data?.data ?? res?.data;
        setMatrix((prev) => {
          if (!prev) return prev;
          const students = prev.students.map((student) => {
            if (String(student.enrollmentId) !== String(enrollmentId)) return student;
            const nextProgress = {
              ...student.progressByModule,
              [moduleId]: {
                ...(student.progressByModule?.[moduleId] || {}),
                moduleId,
                status: payload?.progress?.status || status,
                isCompleted: status === MODULE_STATUS.COMPLETED,
                completionDate: payload?.progress?.completionDate ?? null,
              },
            };
            return {
              ...student,
              progressByModule: nextProgress,
              allModulesComplete: Boolean(payload?.allModulesComplete),
              interviewReadinessAllModulesComplete: Boolean(
                payload?.interviewReadiness?.allModulesComplete
              ),
            };
          });
          const allModulesCompleted = students.filter((s) => s.allModulesComplete).length;
          return {
            ...prev,
            students,
            summary: {
              ...prev.summary,
              allModulesCompleted,
              completionRate:
                students.length > 0
                  ? `${((allModulesCompleted / students.length) * 100).toFixed(1)}%`
                  : '0%',
            },
          };
        });
        if (payload?.allModulesComplete) {
          setSuccess('All course modules completed for this student. Interview readiness updated.');
        } else {
          setSuccess(`Module marked ${status}.`);
        }
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err?.response?.data?.message || 'Could not update module status');
      } finally {
        setSavingKey('');
      }
    },
    []
  );

  return {
    batches,
    selectedBatchId,
    setSelectedBatchId,
    matrix,
    loadingBatches,
    loadingMatrix,
    savingKey,
    error,
    success,
    setSuccess,
    reload: () => loadMatrix(selectedBatchId),
    updateStatus,
  };
}
